import React, { useEffect, useState } from "react";
import { useAPI } from "../../Context/MyContext";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";

function ApiIntegrations() {
  const [userPhoneNumbers, setUserPhoneNumbers] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [userTokens, setUserTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState("");
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [activeTab, setActiveTab] = useState("tokens");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [showConfirm, setShowConfirm] = useState(null);
  const [copiedGen, setCopiedGen] = useState(false);
  const [generatedToken, setGeneratedToken] = useState("");
  const [copied, setCopied] = useState(false);
  const [swaggerData, setSwaggerData] = useState(null);

  const { getPhoneAll, createApiToken, fetchUserTokens, toggleApiTokenStatus, deleteApiToken, swagger } = useAPI();
  const server = process.env.REACT_APP_SERVER_URL;

  const loadSwagger = async () => {
    try {
      const data = await swagger();
      console.log("Swagger JSON:", data);
      setSwaggerData(data);
    } catch (err) {
      console.error("Failed to load Swagger JSON:", err);
    }
  }

  useEffect(() => {
    // loadSwagger();
    if (activeTab === "docs") {
      loadSwagger();
    }
  }, [activeTab]);

  useEffect(() => {
    const loadPhones = async () => {
      try {
        const res = await getPhoneAll();
        const phones = res?.data || [];
        setUserPhoneNumbers(phones);
        if (phones.length > 0) setSelectedPhone(phones[0]);
      } catch (err) {
        console.error(err);
      }
    };
    loadPhones();
  }, []);

  useEffect(() => {
    if (!selectedPhone) return;
    const loadTokens = async () => {
      try {
        setLoading(true);
        const res = await fetchUserTokens(selectedPhone.waba_id);
        setUserTokens(res?.api_tokens || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadTokens();
  }, [selectedPhone]);

  const handleCreateToken = async () => {
    try {
      const res = await createApiToken(selectedPhone.waba_id, tokenName);
      setGeneratedToken(res.token);
      setTokenName("");
      setShowModal(false);
      const updated = await fetchUserTokens(selectedPhone.waba_id);
      setUserTokens(updated.api_tokens || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggle = async (token) => {
    try {
      const action = token.is_active ? "revoke" : "activate";
      await toggleApiTokenStatus(selectedPhone.waba_id, token._id, action);
      const updated = await fetchUserTokens(selectedPhone.waba_id);
      setUserTokens(updated.api_tokens || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (tokenId) => {
    try {
      await deleteApiToken(selectedPhone.waba_id, tokenId);
      setUserTokens((prev) => prev.filter((t) => t._id !== tokenId));
      setShowConfirm(null);
    } catch (err) {
      console.error(err);
    }
  };


  const copy = async (text, setter) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // fallback for HTTP / IP access
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    setter(true);
    setTimeout(() => setter(false), 2000);
  } catch (err) {
    console.error("Copy failed:", err);
  }
};

  return (
    <div className="w-full min-h-screen ">

      {/* HEADER + TOOLBAR + TABS */}
      <div className="mx-6 mt-6 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">

        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">API Integrations</h1>
          <button
            onClick={() => { setShowModal(true); setTokenName(""); }}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Create token
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-3">
          <div className="relative">
            <select
              value={selectedPhone?.phone_number_id || ""}
              onChange={(e) => {
                const phone = userPhoneNumbers.find(p => p.phone_number_id === e.target.value);
                setSelectedPhone(phone);
                setSelectedToken("");
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg pl-3 pr-9 py-2 text-sm text-gray-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[240px]"
            >
              {userPhoneNumbers.map((phone) => (
                <option key={phone.phone_number_id} value={phone.phone_number_id}>
                  {phone.verified_name} — {phone.display_phone_number}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex gap-0 px-6">
          {["tokens", "docs"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm border-b-2 transition-colors capitalize ${activeTab === tab
                  ? "border-blue-600 text-blue-600 font-medium"
                  : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
            >
              {tab === "tokens" ? "API Keys" : "API Docs"}
            </button>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="mx-6 py-5">

        {/* GENERATED TOKEN BANNER */}
        {generatedToken && (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-5">
            <p className="text-sm text-green-700 whitespace-nowrap font-medium">Copy your token:</p>
            <div className="flex flex-1">
              <input
                value={generatedToken}
                readOnly
                className="flex-1 border border-green-300 border-r-0 rounded-l-lg px-3 py-1.5 text-xs font-mono bg-white text-gray-800 focus:outline-none"
              />
              <button
                onClick={() => copy(generatedToken, setCopiedGen)}
                className="bg-green-600 text-white text-xs px-4 rounded-r-lg hover:bg-green-700 transition-colors"
              >
                {copiedGen ? "Copied!" : "Copy"}
              </button>
            </div>
            <button
              onClick={() => setGeneratedToken("")}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ×
            </button>
          </div>
        )}

        {activeTab === "tokens" && (
          <>
            {/* TOKEN TABLE */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
                  Loading tokens...
                </div>
              ) : userTokens.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  No API tokens yet — create one to get started.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Key ID</th>
                      <th className="px-5 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userTokens.map((token) => (
                      <tr
                        key={token._id}
                        className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedToken(`sk_live_${token.key_id}.${token.token_hash}`);
                          setShowTokenModal(true);
                          setCopied(false);
                        }}
                      >
                        <td className="px-5 py-4 font-medium text-gray-900">{token.name}</td>
                        <td className="px-5 py-4 font-mono text-xs text-gray-400 tracking-wide">{token.key_id}</td>
                        <td className="px-5 py-4 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${token.is_active
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                            }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${token.is_active ? "bg-green-500" : "bg-red-400"}`} />
                            {token.is_active ? "Active" : "Revoked"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-xs text-gray-500">
                          {new Date(token.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td
                          className="px-5 py-4 text-right space-x-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleToggle(token)}
                            className="text-blue-600 text-xs px-2.5 py-1.5 rounded-md hover:bg-blue-50 transition-colors"
                          >
                            {token.is_active ? "Revoke" : "Activate"}
                          </button>
                          <button
                            onClick={() => setShowConfirm({ id: token._id, name: token.name })}
                            className="text-red-500 text-xs px-2.5 py-1.5 rounded-md hover:bg-red-50 transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {activeTab === "docs" && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex justify-between items-center px-5 py-3.5 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-800">API Documentation</span>
              {/* <a href={`${server}/api-docs`} target="_blank" rel="noreferrer" className="text-blue-600 text-xs hover:underline">
                Open in new tab ↗
              </a> */}
            </div>
            {/* <iframe src={`${server}/api-docs`} className="w-full h-[680px]" title="Swagger UI" /> */}
            {activeTab === "docs" && (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-4">
                {/* <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-800">
                    API Documentation
                  </span>
                </div> */}

                {swaggerData ? (
                  <SwaggerUI spec={swaggerData} />
                ) : (
                  <div className="text-center py-10 text-gray-400">
                    Loading API docs...
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* VIEW TOKEN MODAL */}
      {showTokenModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowTokenModal(false)}
        >
          <div className="bg-white rounded-xl border border-gray-200 w-[480px] max-w-[calc(100vw-32px)] p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-gray-900">API Token</h2>
              <button
                onClick={() => setShowTokenModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">Copy this token to use in your integration.</p>
            <div className="flex mb-5">
              <input
                value={selectedToken}
                readOnly
                className="flex-1 border border-r-0 border-gray-300 rounded-l-lg px-3 py-2.5 text-xs font-mono bg-gray-50 text-gray-700 focus:outline-none"
              />
              <button
                onClick={() => copy(selectedToken, setCopied)}
                className="bg-blue-600 text-white text-sm px-4 rounded-r-lg hover:bg-blue-700 transition-colors"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowTokenModal(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE TOKEN MODAL */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <div className="bg-white rounded-xl border border-gray-200 w-[400px] max-w-[calc(100vw-32px)] p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Create API token</h2>
            <p className="text-sm text-gray-500 mb-5">Give your token a descriptive name so you can identify it later.</p>
            <input
              type="text"
              autoFocus
              placeholder="e.g. Production integration"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && tokenName && handleCreateToken()}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateToken}
                disabled={!tokenName}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Create token
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {showConfirm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={(e) => e.target === e.currentTarget && setShowConfirm(null)}
        >
          <div className="bg-white rounded-xl border border-gray-200 w-[400px] max-w-[calc(100vw-32px)] p-6">
            <div className="flex gap-3 items-start mb-5">
              <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">Delete token</h2>
                <p className="text-sm text-gray-500">
                  Permanently delete <span className="font-medium text-gray-800">{showConfirm.name}</span>?
                  Any integrations using this token will stop working immediately.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showConfirm.id)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApiIntegrations;